import { baseApi } from '@/shared/lib/baseApi';

export interface LoginPayload {
  username: string;
  password?: string;
}

export interface User {
  id: string;
  name?: string;
  email: string;
  role: string;
  apiKey?: string;
  enterpriseId?: string | null;
  loginMethodCode?: string | null;
  roleCode?: string;
}

export interface OtpVerifyPayload {
  otpId: string;
  code: string;
  username: string;
}

export type LoginSuccessResponse = {
  type: 'login';
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  refreshExpiresIn?: number;
  user: {
    id: string;
    roleCode: string;
    loginMethodCode?: string | null;
  };
};

export type LoginOtpResponse = {
  type: 'otp';
  otpId: string;
};

export type LoginQrResponse = {
  type: 'esimProvision';
  signature: string;
};

export type LoginEmailVerificationResponse = {
  type: 'emailVerification';
  email: string;
};

export type ResendOtpResponse = {
  type: 'otp';
  otpId: string;
  retryAfterSeconds?: number;
};

export interface LoginMethodLookupResponse {
  username: string;
  otpId: string | null;
  loginMethodCode: string | null;
  loginMethodName: string | null;
  loginMethodDescription: string | null;
  signatureCode?: string | null;
  isSso?: boolean;
  redirectUrl?: string;
  state?: string;
}

export interface LoginMethodLookupPayload {
  username: string;
}

export interface SetupPasswordPayload {
  token: string;
  password: string;
}

export interface SetupPasswordResponse {
  statusCode: number;
  statusMessage: string;
  message: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
  confirmPassword: string;
  username?: string;
}

export interface ResetPasswordResponse {
  statusCode: number;
  statusMessage: string;
  message: string;
}

export interface ResetPasswordRequestPayload {
  username?: string;
  email?: string;
}

export interface ResetPasswordRequestResponse {
  statusCode: number;
  statusMessage: string;
  message: string;
}

export interface UserProfileResponse {
  id: number;
  userId: number;
  username: string;
  email: string;
  role: string;
  name: string;
  permissions: string[];
  enterpriseId: string | null;
  groupId: number;
  type: string;
  brandName: string | null;
  apiKey: string | null;
}

export type LoginResponse =
  | LoginSuccessResponse
  | LoginOtpResponse
  | LoginQrResponse
  | LoginEmailVerificationResponse;

export interface LogoutApiResponse {
  statusMessage: string; // e.g. 'response.USER_LOGOUT_SUCCESS'
  data: {
    success: boolean;
    message: string;
    logoutUrl?: string;
  };
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginPayload>({
      query: (credentials) => ({
        url: '/api/auth/user/login',
        method: 'POST',
        body: credentials,
      }),
      transformResponse: (response: ApiResponse<LoginResponse>) =>
        response.data,
    }),

    verifyOtp: builder.mutation<LoginSuccessResponse, OtpVerifyPayload>({
      query: (body) => ({
        url: '/api/auth/user/login/otp',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<LoginSuccessResponse>) =>
        response.data,
    }),

    resendVerificationEmail: builder.mutation<
      { message: string; retryAfterSeconds?: number },
      { email: string }
    >({
      query: (body) => ({
        url: '/api/enterprises/resend-verification-email',
        method: 'POST',
        body,
      }),
    }),

    resendOtp: builder.mutation<ResendOtpResponse, { username: string }>({
      query: (body) => ({
        url: '/api/auth/user/resend-otp',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<ResendOtpResponse>) =>
        response.data,
    }),

    lookupLoginMethod: builder.mutation<
      LoginMethodLookupResponse,
      LoginMethodLookupPayload
    >({
      query: (body) => ({
        url: '/api/auth/user/login/method',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: ApiResponse<
          LoginMethodLookupResponse & {
            otp_id?: string | null;
            activation_signature_hash?: string | null;
          }
        >
      ) => ({
        ...response.data,
        otpId: response.data.otpId ?? response.data.otp_id ?? null,
        signatureCode:
          response.data.signatureCode ??
          response.data.activation_signature_hash ??
          null,
      }),
    }),

    exchangeCode: builder.mutation<LoginSuccessResponse, { code: string }>({
      query: (body) => ({
        url: '/api/auth/sso/exchange',
        method: 'POST',
        body,
      }),
    }),
    // inside endpoints: (builder) => ({ ... })
    logout: builder.mutation<LogoutApiResponse, { loginMethod?: string }>({
      query: (body) => ({
        url: '/api/auth/user/logout',
        method: 'POST',
        body,
      }),
    }),
    setupPassword: builder.mutation<
      SetupPasswordResponse,
      SetupPasswordPayload
    >({
      query: (body) => ({
        url: '/api/auth/user/setup-password',
        method: 'POST',
        body,
      }),
    }),

    resetPassword: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordPayload
    >({
      query: (body) => ({
        url: '/api/auth/user/reset-password/by-token',
        method: 'POST',
        body,
      }),
    }),

    resetPasswordRequest: builder.mutation<
      ResetPasswordRequestResponse,
      ResetPasswordRequestPayload
    >({
      query: (body) => ({
        url: '/api/auth/user/reset-password/request',
        method: 'POST',
        body,
      }),
    }),

    getMe: builder.query<UserProfileResponse, void>({
      query: () => ({
        url: '/api/auth/user/me',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<UserProfileResponse>) =>
        response.data,
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyOtpMutation,
  useResendVerificationEmailMutation,
  useResendOtpMutation,
  useLookupLoginMethodMutation,
  useSetupPasswordMutation,
  useResetPasswordMutation,
  useResetPasswordRequestMutation,
  useExchangeCodeMutation,
  useLogoutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
} = authApi;
